import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import Sidebar, { BottomNav } from "@/components/Sidebar";
import { useState } from "react";
import { useNotifications } from "@/hooks/useNotifications";
import Dashboard from "@/pages/Dashboard";
import ModelsPage from "@/pages/ModelsPage";
import InventoryPage from "@/pages/InventoryPage";
import TeamPage from "@/pages/TeamPage";
import TasksPage from "@/pages/TasksPage";
import ProjectsPage from "@/pages/ProjectsPage";
import FinancePage from "@/pages/FinancePage";
import FinanceMonthPage from "@/pages/FinanceMonthPage";
import RefundPage from "@/pages/RefundPage";
import ExpensesPage from "@/pages/ExpensesPage";
import CustomerPaymentsPage from "@/pages/CustomerPaymentsPage";
import StaffLinePage from "@/pages/StaffLinePage";
import SettingsPage from "@/pages/SettingsPage";
import LogisticPage from "@/pages/LogisticPage";
import MyProfilePage from "@/pages/MyProfilePage";
import BillingPage from "@/pages/BillingPage";
import PartiesPage from "@/pages/PartiesPage";
import TaxReportsPage from "@/pages/TaxReportsPage";
import VenuePage from "@/pages/VenuePage";
import DocumentsPage from "@/pages/DocumentsPage";
import ChatGuidePage from "@/pages/ChatGuidePage";
import LoginPage from "@/pages/LoginPage";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import InstallBanner from "@/components/InstallBanner";

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2" style={{ borderColor: '#a3e635' }}></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Main Layout with Sidebar
function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();
  // Init push notifications for logged-in user (asks permission once, handles foreground toasts)
  // eslint-disable-next-line no-unused-vars
  const _notif = useNotifications(user?.id);
  return (
    <div className="app-layout">
      <Sidebar externalOpen={sidebarOpen} onExternalClose={() => setSidebarOpen(false)} />
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/models" element={<ModelsPage />} />
          <Route path="/inventory" element={<InventoryPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/finance" element={<FinancePage />} />
          <Route path="/finance/month/:monthKey" element={<FinanceMonthPage />} />
          <Route path="/refund" element={<RefundPage />} />
          <Route path="/expenses" element={<ExpensesPage />} />
          <Route path="/customer-payments" element={<CustomerPaymentsPage />} />
          <Route path="/staff-line" element={<StaffLinePage />} />
          <Route path="/logistic" element={<LogisticPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/profile" element={<MyProfilePage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/parties" element={<PartiesPage />} />
          <Route path="/tax-reports" element={<TaxReportsPage />} />
          <Route path="/venue" element={<VenuePage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/chat-guide" element={<ChatGuidePage />} />
        </Routes>
      </main>
      <BottomNav onMoreClick={() => setSidebarOpen(true)} />
      <InstallBanner />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/*" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
